import { useEffect, useState } from "react";
import { X, Calendar, Clock, User, Wrench, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { offlineSafe } from "@/lib/api";
import { CustomerPicker, VehiclePicker } from "@/pages/estimates/components/Pickers";
import { STATUS_META, STATUS_ORDER, isoNaive, toDate, CALENDAR_DEFAULTS, normalizeStatus, allowedWindow, isWithinAllowedWindow, DEFAULT_WORKING_HOURS } from "../lib";

/**
 * Create / edit / view an appointment.
 *
 * Props:
 *   open           — bool
 *   onClose        — () => void
 *   initial        — full appointment object (if editing)   OR
 *   defaultStart   — Date       (if creating from calendar click / drag)
 *   defaultEnd     — Date       (if creating from drag-across time slots)
 *   onSaved        — (appointmentDoc) => void
 *   onConvert      — (appointmentDoc) => void         // pop convert dialog
 *   assignees      — Array<{id,name,email,role}>
 */
export default function AppointmentDialog({
  open, onClose, initial, defaultStart, defaultEnd, onSaved, onConvert,
  assignees = [], workingHours = DEFAULT_WORKING_HOURS,
}) {
  const isEdit = !!initial?.id;
  // JC-linked appointments are locked from further edits. Legacy docs with
  // status "converted" also carry converted_to_job_card_id.
  const isLocked = !!initial?.converted_to_job_card_id;

  const [form, setForm] = useState(() => {
    const now = defaultStart ? new Date(defaultStart) : new Date();
    const dur = defaultEnd ? Math.max(15, Math.round((new Date(defaultEnd) - new Date(defaultStart)) / 60000)) : 60;
    return {
      customer_id: initial?.customer_id || null,
      customer_snapshot: initial?.customer_snapshot || null,
      vehicle_id: initial?.vehicle_id || null,
      vehicle_snapshot: initial?.vehicle_snapshot || null,
      scheduled_at: initial?.scheduled_at || isoNaive(now),
      duration_min: initial?.duration_min || dur,
      assignee_id: initial?.assignee_id || "",
      service_notes: initial?.service_notes || "",
      complaints: initial?.complaints || "",
      status: normalizeStatus(initial?.status) || "scheduled",
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const dt = toDate(form.scheduled_at);
  const dateStr = dt ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}` : "";
  const timeStr = dt ? `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}` : "";

  const setDate = (v) => {
    const cur = toDate(form.scheduled_at) || new Date();
    const [y, m, d] = v.split("-").map(Number);
    cur.setFullYear(y, m - 1, d);
    setForm({ ...form, scheduled_at: isoNaive(cur) });
  };
  const setTime = (v) => {
    const cur = toDate(form.scheduled_at) || new Date();
    const [h, mm] = v.split(":").map(Number);
    cur.setHours(h, mm, 0, 0);
    setForm({ ...form, scheduled_at: isoNaive(cur) });
  };

  const canSave =
    form.customer_snapshot?.name &&
    form.customer_snapshot?.phone &&
    form.scheduled_at;

  // Working-hours window (open − 2h → close + 2h). Surfaced under the time
  // picker as a hint and enforced on save. Prevents AM/PM mistakes.
  const window = allowedWindow(workingHours);
  const timeOutOfWindow = form.scheduled_at && !isWithinAllowedWindow(form.scheduled_at, workingHours);

  const save = async () => {
    if (!canSave) {
      setError("Customer name, mobile, and appointment time are required");
      return;
    }
    if (timeOutOfWindow) {
      setError(
        `Appointments must be within working hours ±2h (${window.earliest}–${window.latest}). ` +
        `Update hours in Settings if needed.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        customer_id: form.customer_id || form.customer_snapshot?.id,
        vehicle_id: form.vehicle_id || form.vehicle_snapshot?.id,
        customer_snapshot: form.customer_snapshot,
        vehicle_snapshot: form.vehicle_snapshot,
        service_notes: form.service_notes || "",
        complaints: form.complaints || "",
        scheduled_at: form.scheduled_at,
        duration_min: Number(form.duration_min || 60),
        assignee_id: form.assignee_id || null,
        status: form.status || "scheduled",
      };
      let saved;
      if (isEdit) {
        const r = await offlineSafe("PATCH", `/appointments/${initial.id}`, payload, `Update ${initial.number}`);
        if (r._queued) {
          toast.info("Saved offline — will sync when you're back online.");
          saved = { ...initial, ...payload, _queued: true };
        } else {
          saved = r;
          toast.success("Appointment updated");
        }
      } else {
        const resp = await api.post("/appointments", payload);
        saved = resp.data;
        toast.success(`Appointment ${saved.number} created`);
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save appointment");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!isEdit || !window.confirm("Cancel this appointment?")) return;
    try {
      await api.post(`/appointments/${initial.id}/cancel`);
      toast.success("Appointment cancelled");
      onSaved?.({ ...initial, status: "cancelled" });
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const del = async () => {
    if (!isEdit || !window.confirm("Delete this appointment permanently?")) return;
    try {
      await api.delete(`/appointments/${initial.id}`);
      toast.success("Appointment deleted");
      onSaved?.(null, initial.id);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const meta = STATUS_META[form.status] || STATUS_META.scheduled;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="appt-dialog-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        data-testid="appt-dialog"
      >
        {/* Header */}
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#a8dadc]/40 grid place-items-center text-[#457b9d]">
                <Calendar size={17} />
              </div>
              <div>
                <h2 className="text-[17px] font-bold text-[#1d3557] leading-tight">
                  {isEdit ? `Edit Appointment` : "New Appointment"}
                </h2>
                <p className="text-[11.5px] text-[#667085]">
                  {isEdit && initial?.number
                    ? <span className="font-mono">{initial.number}</span>
                    : "Book a customer slot"}
                  {" "}
                  {isEdit && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider ${meta.chip}`}>
                      {meta.label}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085] hover:text-[#1d3557]"
            data-testid="appt-dialog-close"
          >
            <X size={17} />
          </button>
        </header>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {isLocked && (
            <div className="rounded-lg border border-[#1d3557]/20 bg-[#a8dadc]/15 px-3 py-2.5 text-[12px] text-[#1d3557] flex items-center gap-2" data-testid="appt-locked-banner">
              <Wrench size={13} />
              <span>
                This appointment has been converted to job card
                {initial?.converted_to_job_card_id && (
                  <a href={`/job-cards/${initial.converted_to_job_card_id}`} className="ml-1 font-bold underline">
                    View job card
                  </a>
                )}
                . Editing is disabled.
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2.5 text-[12px] text-[#e63946]" data-testid="appt-dialog-error">
              {error}
            </div>
          )}

          {/* Customer */}
          <section>
            <Label>Customer *</Label>
            <div className="mt-1">
              <CustomerPicker
                value={form.customer_snapshot}
                disabled={isLocked}
                onChange={(c) => setForm({ ...form, customer_id: c?.id || null, customer_snapshot: c, vehicle_id: null, vehicle_snapshot: null })}
              />
            </div>
          </section>

          {/* Vehicle */}
          <section>
            <Label>Vehicle</Label>
            <div className="mt-1">
              <VehiclePicker
                value={form.vehicle_snapshot}
                customerId={form.customer_id || form.customer_snapshot?.id}
                disabled={isLocked}
                onChange={(v) => setForm({ ...form, vehicle_id: v?.id || null, vehicle_snapshot: v })}
              />
            </div>
          </section>

          {/* Date/time + duration + assignee */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Date *">
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDate(e.target.value)}
                disabled={isLocked}
                data-testid="appt-date"
                className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              />
            </Field>
            <Field label="Time *">
              <input
                type="time"
                value={timeStr}
                onChange={(e) => setTime(e.target.value)}
                step="900"
                min={window.earliest}
                max={window.latest}
                disabled={isLocked}
                data-testid="appt-time"
                className={`w-full h-10 px-2 rounded-md border text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC] ${
                  timeOutOfWindow ? "border-[#e63946]" : "border-[#E4E7EC]"
                }`}
              />
              <p
                className={`text-[10.5px] mt-1 ${timeOutOfWindow ? "text-[#e63946] font-semibold" : "text-[#667085]"}`}
                data-testid="appt-time-hint"
              >
                Allowed window: {window.earliest} – {window.latest} (working hours ±2h)
              </p>
            </Field>
            <Field label="Duration">
              <select
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })}
                disabled={isLocked}
                data-testid="appt-duration"
                className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              >
                {[15, 30, 45, 60, 90, 120, 180, 240].map((m) => (
                  <option key={m} value={m}>{m} min</option>
                ))}
              </select>
            </Field>
            <Field label="Advisor">
              <select
                value={form.assignee_id || ""}
                onChange={(e) => setForm({ ...form, assignee_id: e.target.value || null })}
                disabled={isLocked}
                data-testid="appt-assignee"
                className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              >
                <option value="">Unassigned</option>
                {assignees.map((a) => (
                  <option key={a.id} value={a.id}>
                    {(a.name || a.email)}{a.role ? ` · ${a.role}` : ""}
                  </option>
                ))}
              </select>
            </Field>
          </section>

          {/* Complaint + notes */}
          <section>
            <Field label="Customer complaint (goes to the job card on conversion)">
              <textarea
                rows={2}
                value={form.complaints}
                onChange={(e) => setForm({ ...form, complaints: e.target.value })}
                disabled={isLocked}
                data-testid="appt-complaints"
                placeholder="Engine noise, service due, brake issue…"
                className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              />
            </Field>
          </section>
          <section>
            <Field label="Internal note (not visible to customer)">
              <textarea
                rows={2}
                value={form.service_notes}
                onChange={(e) => setForm({ ...form, service_notes: e.target.value })}
                disabled={isLocked}
                data-testid="appt-notes"
                placeholder="Prepare oil filter, call before arrival…"
                className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              />
            </Field>
          </section>

          <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                disabled={isLocked}
                data-testid="appt-status"
                className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none disabled:bg-[#F7F9FC]"
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </Field>
          </section>
        </div>

        {/* Footer */}
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex gap-2">
            {isEdit && !isLocked && (
              <>
                <button
                  type="button"
                  onClick={cancel}
                  className="h-9 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#667085] hover:bg-white"
                  data-testid="appt-cancel-btn"
                >
                  Cancel appointment
                </button>
                <button
                  type="button"
                  onClick={del}
                  className="h-9 px-3 rounded-md text-[12px] font-semibold text-[#e63946] hover:bg-[#e63946]/10"
                  data-testid="appt-delete-btn"
                >
                  Delete
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            {isEdit && !isLocked && (
              <button
                type="button"
                onClick={() => onConvert?.(initial)}
                className="h-10 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                data-testid="appt-convert-btn"
              >
                <Wrench size={13} /> Convert to Job Card
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white"
              data-testid="appt-close-btn"
            >
              Close
            </button>
            {!isLocked && (
              <button
                type="button"
                onClick={save}
                disabled={!canSave || saving || timeOutOfWindow}
                className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="appt-save-btn"
              >
                <Check size={13} />
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create appointment"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{children}</span>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </label>
  );
}
